import React, { useEffect, useState, useMemo } from 'react';
import { Box, Container, Paper, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel, TableContainer, Button, Switch, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../context/UserContext';
import { BACKEND_URL } from '../utils/constants';
import { useTheme } from '@mui/material/styles';
import { colors } from '../theme';

import { ConstructionLoader } from '../components/ConstructionLoader';
interface UserRow {
  id: string;
  email: string;
  is_active: boolean;
  subscription_status?: string | null;
  current_period_end?: number | string | null;
  cancel_at_period_end?: boolean | null;
  created_at?: string | null;
  model_count?: number;
  version_count?: number;
  last_version_created_at?: string | null;
  plan_tier?: string | null;
  last_login?: string | null;
  is_paying?: boolean;
}

type SortKey = 'email' | 'model_count' | 'version_count' | 'created_at' | 'last_version_created_at' | 'subscription' | 'plan_tier' | 'last_login' | 'is_paying' | 'is_active';

const UserMgmt: React.FC = () => {
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { user: me } = useUser();
  const [users, setUsers] = useState<UserRow[]>([]);
  // Active toggle (SEC-05, batch 1): is_active is enforced by requires_auth,
  // so flipping it off locks that account out on its next request.
  const [pendingDeactivate, setPendingDeactivate] = useState<UserRow | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [q, setQ] = useState('');
  const [orderBy, setOrderBy] = useState<SortKey>('email');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const load = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      const r = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await r.json();
      if (Array.isArray(data)) setUsers(data as UserRow[]);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently]);

  const refreshFromStripe = async () => {
    try {
      setRefreshing(true);
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      await fetch(`${BACKEND_URL}/api/admin/users/refresh_subscriptions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      await load();
    } catch (e) {
      // noop
    } finally {
      setRefreshing(false);
    }
  };

  const isSelf = (u: UserRow) => !!me?.id && String(me.id) === String(u.id);

  const setActive = async (u: UserRow, next: boolean) => {
    if (!next && isSelf(u)) return; // guarded in the UI and again on the server
    try {
      setToggleError(null);
      setTogglingId(u.id);
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      const r = await fetch(`${BACKEND_URL}/api/admin/users/${u.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: next })
      });
      let data: any = {};
      try { data = await r.json(); } catch {}
      if (!r.ok) {
        setToggleError(data?.error || `Could not update ${u.email} (${r.status})`);
        return;
      }
      setUsers(prev => prev.map(row => row.id === u.id ? { ...row, is_active: !!data.is_active } : row));
    } catch (e) {
      setToggleError(`Could not update ${u.email}`);
    } finally {
      setTogglingId(null);
    }
  };

  const onToggleActive = (u: UserRow, next: boolean) => {
    if (next) {
      void setActive(u, true);
    } else {
      setPendingDeactivate(u); // confirm before locking someone out
    }
  };

  const confirmDeactivate = async () => {
    const u = pendingDeactivate;
    setPendingDeactivate(null);
    if (u) await setActive(u, false);
  };

  const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }) : '—';

  const effectiveSubStatus = (u: UserRow) => {
    const raw = u.subscription_status || null;
    let cpeMs: number | null = null;
    if (typeof u.current_period_end === 'number') {
      cpeMs = u.current_period_end > 1e12 ? u.current_period_end : u.current_period_end * 1000;
    } else if (typeof u.current_period_end === 'string') {
      const t = Date.parse(u.current_period_end);
      cpeMs = isNaN(t) ? null : t;
    }
    const now = Date.now();
    const isCancelAtEnd = u.cancel_at_period_end === true;
    if (raw === 'active') return 'active';
    if (raw === 'trialing' && !isCancelAtEnd && cpeMs && cpeMs < now) {
      return 'active';
    }
    return raw || '—';
  };

  const handleRequestSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = users.filter(u => !q || u.email.toLowerCase().includes(q.toLowerCase()));

  const sortedUsers = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (orderBy) {
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'model_count':
          aVal = a.model_count ?? -1;
          bVal = b.model_count ?? -1;
          break;
        case 'version_count':
          aVal = a.version_count ?? -1;
          bVal = b.version_count ?? -1;
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'last_version_created_at':
          aVal = a.last_version_created_at ? new Date(a.last_version_created_at).getTime() : 0;
          bVal = b.last_version_created_at ? new Date(b.last_version_created_at).getTime() : 0;
          break;
        case 'subscription':
          aVal = effectiveSubStatus(a);
          bVal = effectiveSubStatus(b);
          break;
        case 'plan_tier':
          aVal = a.plan_tier || 'freemium';
          bVal = b.plan_tier || 'freemium';
          break;
        case 'last_login':
          aVal = a.last_login ? new Date(a.last_login).getTime() : 0;
          bVal = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'is_paying':
          aVal = a.is_paying ? 1 : 0;
          bVal = b.is_paying ? 1 : 0;
          break;
        case 'is_active':
          aVal = a.is_active === false ? 0 : 1;
          bVal = b.is_active === false ? 0 : 1;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, orderBy, order]);

  const columns: { key: SortKey; label: string }[] = [
    { key: 'email', label: 'Email' },
    { key: 'model_count', label: 'Models' },
    { key: 'version_count', label: 'Total Model Versions' },
    { key: 'created_at', label: 'Account Created' },
    { key: 'last_login', label: 'Last Login' },
    { key: 'last_version_created_at', label: 'Most Recent Model Update' },
    { key: 'subscription', label: 'Subscription' },
    { key: 'plan_tier', label: 'Tier' },
    { key: 'is_paying', label: 'Paying' },
    { key: 'is_active', label: 'Active' },
  ];

  return (
    <Container maxWidth={false} sx={{ mt: { xs: 2, md: 6 }, width: '100%', mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>User Information</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button size="small" variant="outlined" onClick={refreshFromStripe} disabled={refreshing || loading}>
              {refreshing ? 'Refreshing…' : 'Refresh from Stripe'}
            </Button>
            <TextField size="small" placeholder="Search email" value={q} onChange={e => setQ(e.target.value)} />
          </Box>
        </Box>
        {toggleError && (
          <Alert severity="error" onClose={() => setToggleError(null)} sx={{ mb: 2 }}>{toggleError}</Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><ConstructionLoader /></Box>
        ) : (
          <TableContainer
            sx={{
              borderRadius: `${theme.shape.borderRadius}px`,
              border: `1px solid ${colors.grey[300]}`,
              boxShadow: theme.shadows[1],
              overflowX: 'auto',
              width: '100%'
            }}
          >
            <Table
              sx={{
                minWidth: 900,
                '& thead th': {
                  fontWeight: 700,
                  backgroundColor: 'rgba(255,255,255,0.22)'
                },
                '& tbody td': {
                  borderBottom: '1px solid rgba(0,0,0,0.06)'
                },
                '& tbody tr:hover': {
                  backgroundColor: 'rgba(255,255,255,0.35)'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleRequestSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map(u => (
                  <TableRow key={u.id}>
                    <TableCell sx={{ wordBreak: 'break-word', minWidth: 180 }}>{u.email}</TableCell>
                    <TableCell>{u.model_count ?? 0}</TableCell>
                    <TableCell>{u.version_count ?? 0}</TableCell>
                    <TableCell>{fmt(u.created_at)}</TableCell>
                    <TableCell>{fmt(u.last_login)}</TableCell>
                    <TableCell>{fmt(u.last_version_created_at)}</TableCell>
                    <TableCell>{effectiveSubStatus(u)}</TableCell>
                    <TableCell>{u.plan_tier || 'freemium'}</TableCell>
                    <TableCell sx={{ fontWeight: u.is_paying ? 700 : 400 }}>{u.is_paying ? 'Yes' : '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={isSelf(u) ? 'You cannot deactivate your own account' : (u.is_active === false ? 'Deactivated: this user cannot log in' : 'Active')}>
                        <span>
                          <Switch
                            size="small"
                            checked={u.is_active !== false}
                            disabled={togglingId === u.id || (isSelf(u) && u.is_active !== false)}
                            onChange={(e) => onToggleActive(u, e.target.checked)}
                            inputProps={{ 'aria-label': `Active: ${u.email}` }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Dialog open={!!pendingDeactivate} onClose={() => setPendingDeactivate(null)}>
        <DialogTitle>Deactivate this account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeactivate?.email} will be signed out and unable to log in until you switch the account back on. Their models and data are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeactivate(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeactivate}>Deactivate</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserMgmt;
